import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import { Modal, Form, FormGroup, TextArea } from "carbon-components-react";

import {
  ConditionShape,
  ConditionStatus,
  MessageSenders,
  MessageShape,
} from "@eave/shared/types/api";
import { formatDateTimeString } from "@eave/shared/utils/date-formatter";

import APIClient from "web_modules/APIClient";
import { updateCondition } from "underwriter_dashboard/shared/Conditions/APIHelpers";
import { DocumentUploader } from "web_modules/DocumentUploader";
import BaseTable from "web_modules/shared/BaseTable";
import LabeledStructuredList from "../../shared/LabeledStructuredList";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

const conditionStatusToDisplayName = {
  [ConditionStatus.ReadyToReview]: "Sent to HLHL for review",
  [ConditionStatus.Requested]: "Please respond",
  [ConditionStatus.Cleared]: "Complete",
  [ConditionStatus.NotApplicable]: "Complete",
};

const isSettlementAgent = (email: string) => {
  const settlementAgentEmails = [
    "shawna@homelight.com",
    "melissa.hartwick@homelight.com",
    "blanca@homelight.com",
  ];

  return settlementAgentEmails.indexOf(email.toLowerCase()) !== -1;
};

const filterAndSortMessages = (messages: MessageShape[]): MessageShape[] =>
  messages
    .sort((m1, m2) => {
      const a = moment(m1.created_at);
      const b = moment(m2.created_at);
      return a.isBefore(b) ? 1 : -1;
    })
    .filter(({ sender_type }) => sender_type && sender_type !== MessageSenders.Borrower);

const ConditionsTable = ({ appID }) => {
  const [conditions, setConditions] = useState<ConditionShape[]>([]);
  useEffect(() => {
    APIClient.get(`/sa/loan_applications/${appID}/conditions`)
      .then(resp => {
        const { conditions: c } = resp as {
          conditions: ConditionShape[];
        };
        setConditions(
          c.sort((c1, c2) => {
            const a = moment(c1.created_at);
            const b = moment(c2.created_at);
            return a.isBefore(b) ? -1 : 1;
          }),
        );
      })
      .catch(() => {
        window.alert(
          `There was an error fetching conditions associated with loan app id: ${appID}. Please contact eng.`,
        );
      });
  }, [appID]);

  // Document upload Modal:
  const [documentModalCondition, setDocumentModalCondition] = useState<ConditionShape | null>(null);
  const onConditionModalSubmit = () => {
    updateCondition({ ...documentModalCondition, status: ConditionStatus.ReadyToReview }).then(
      ({ condition: updated }) => {
        setConditions(
          conditions.map(c => (c.id === documentModalCondition.id ? { ...updated } : c)),
        );
        setDocumentModalCondition(null);
      },
    );
  };
  const DocumentUploadModal = documentModalCondition && (
    <Modal
      open={!!documentModalCondition}
      modalHeading="Upload a document for this condition"
      primaryButtonText="Send for review"
      secondaryButtonText="Cancel"
      onBlur={() => {
        setDocumentModalCondition(null);
      }}
      onRequestClose={() => {
        setDocumentModalCondition(null);
      }}
      onRequestSubmit={onConditionModalSubmit}
    >
      <h3>{documentModalCondition.condition}</h3>
      <br />
      <DocumentUploader
        prompt="Drag and drop your document(s) here"
        conditionID={documentModalCondition.id}
        applicationID={appID}
        documentType={documentModalCondition.document_type}
        documents={documentModalCondition.documents}
        ownerEntityType={documentModalCondition.owner_entity_type}
        ownerEntityID={documentModalCondition.owner_entity_id}
        // TODO : make this better and don't required a hard refresh. On upload just returns the document id and not the full documnet, so we can't update the documents [] with the uploaded doc
        onUpload={() =>
          alert(
            "upload was successful. Refresh page if you'd like to see the current list of documents you've uploaded.",
          )
        }
        updateApplication={() => {}}
      />
    </Modal>
  );

  // Comment Modal
  const [commentModalCondition, setCommentModalCondition] = useState<ConditionShape | null>(null);
  const [commentText, setCommentText] = useState("");
  const [invalidComment, setInvalidComment] = useState(false);

  const closeCommentModal = () => {
    setCommentText("");
    setInvalidComment(false);
    setCommentModalCondition(null);
  };

  const updateCommentText = e => {
    setCommentText(e.target.value);
    if (commentText.trim() === "") {
      setInvalidComment(false);
    }
  };

  const onMessageModalSubmit = () => {
    if (commentText.trim() === "") {
      setInvalidComment(true);
      return;
    }
    updateCondition({ ...commentModalCondition, status: ConditionStatus.ReadyToReview })
      .then(() =>
        APIClient.post(`/messages/condition/${commentModalCondition.id}`, {
          message: commentText,
          internal: true,
        }),
      )
      .then(resp => {
        const { message: newMsg } = resp as { message: MessageShape };
        setConditions(
          conditions.map(c =>
            c.id === commentModalCondition.id
              ? {
                  ...commentModalCondition,
                  status: ConditionStatus.ReadyToReview, // Hack: since setState is async
                  messages: commentModalCondition.messages
                    ? [...commentModalCondition.messages, newMsg]
                    : [newMsg],
                }
              : c,
          ),
        );
      })
      .catch(() => {
        alert("There was a problem saving your comment. Please contact Eng.");
      })
      .finally(() => {
        setCommentText("");
        setInvalidComment(false);
        closeCommentModal();
      });
  };

  const CommentModal = commentModalCondition && (
    <Modal
      open={!!commentModalCondition}
      modalHeading="Add a comment to this condition"
      primaryButtonText="Send comment"
      secondaryButtonText="Cancel"
      onBlur={closeCommentModal}
      onRequestClose={closeCommentModal}
      onRequestSubmit={onMessageModalSubmit}
    >
      <p>{commentModalCondition.condition}</p>
      <br />
      <Form>
        <FormGroup legendText="Add a comment">
          <TextArea
            id="comment-text"
            name="comment-text"
            labelText=""
            value={commentText}
            invalid={invalidComment}
            invalidText="Text is required"
            onChange={updateCommentText}
          />
        </FormGroup>
      </Form>
      <br />
      {commentModalCondition.messages && (
        <LabeledStructuredList
          colHeaders={[
            { displayName: "Comment", dataKey: "message" },
            { displayName: "Comment sender", dataKey: "sender" },
            { displayName: "Date and time", dataKey: "timestamp" },
          ]}
          rowData={
            !commentModalCondition.messages
              ? []
              : filterAndSortMessages(commentModalCondition.messages).map(
                  ({ created_at, message, sender_name, sender_email }) => ({
                    message,
                    sender: isSettlementAgent(sender_email)
                      ? `${sender_name} at HLE`
                      : `${sender_name} at HLHL`,
                    timestamp: formatDateTimeString(created_at),
                  }),
                )
          }
        />
      )}
    </Modal>
  );

  return (
    <>
      {DocumentUploadModal}
      {CommentModal}
      <BaseTable
        disableAllControls
        title="Conditions"
        headerData={makeHeaderData({
          condition: "Condition",
          status: "Status",
          comment: "Comment",
          documents: "Upload documents",
        })}
        rowData={conditions.map(condition => {
          return {
            id: condition.id.toString(),
            condition: <td key={condition.condition}>{condition.condition}</td>,
            status: (
              <td key={condition.status}>{conditionStatusToDisplayName[condition.status]}</td>
            ),
            comment: (
              <td key={`${condition.id}-comment`}>
                {/* CARBON TODO: should use a button or data table's overflow edit. Avoiding the button since
              it skews the design
             */}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                <a
                  onClick={() =>
                    setCommentModalCondition(conditions.find(c => c.id === condition.id))
                  }
                >
                  Leave a comment
                </a>
              </td>
            ),
            documents: (
              <td key={`${condition.id.toString()}-documents`}>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                <a
                  onClick={() => {
                    setDocumentModalCondition(conditions.find(c => c.id === condition.id));
                  }}
                >
                  upload doc
                </a>
              </td>
            ),
          };
        })}
      />
    </>
  );
};

export default ConditionsTable;
